import styled from "@emotion/styled";
import { Column, Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import { useStores } from "@src/stores";
import React from "react";
import BN from "@src/utils/BN";
import { IMarket } from "@src/constants";
import CircularProgressbar from "@components/CircularProgressbar";
import Skeleton from "react-loading-skeleton";
import Tooltip from "@components/Tooltip";
import { ReactComponent as Info } from "@src/assets/icons/info.svg";
import useWindowSize from "@src/hooks/useWindowSize";

interface IProps {
  market: IMarket;
}

const Root = styled.div`
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.card.background};
  padding: 20px;
  display: flex;
  flex-direction: column;
  margin-top: 40px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }
`;

const HealthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  padding-top: 24px;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
    justify-items: end;
    width: 100%;
    padding-top: 0;
  }
`;

const promisesUpdateSpeed = 100;

const accountHealthInfo = `
Health Factor indicates how much you have borrowed relative to your borrow limit. Your Account Health goes up when you supply assets or repay debt and goes down when you borrow. If your Account Health goes below 0%, your loan will be partially liquidated with a liquidation penalty deducted from your collateral.
`;

const AccountCard: React.FC<IProps> = ({ market }) => {
  const { lendStore, accountStore } = useStores();
  const { width } = useWindowSize();

  const [totalAccountSupply, setTotalAccountSupply] = React.useState(
    new BN(-2)
  );
  const fetchTotalAccountSupply = async () => {
    if (totalAccountSupply.eq(-2)) {
      setTotalAccountSupply(
        await lendStore.getTotalAccountSupply(market.address)
      );
    }
    while (totalAccountSupply.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalAccountSupply(
        await lendStore.getTotalAccountSupply(market.address)
      );
    }
  };

  const [totalAccountBorrow, setTotalAccountBorrow] = React.useState(
    new BN(-2)
  );
  const fetchTotalAccountBorrow = async () => {
    if (totalAccountBorrow.eq(-2)) {
      setTotalAccountBorrow(
        await lendStore.getTotalAccountBorrow(market.address)
      );
    }
    while (totalAccountBorrow.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalAccountBorrow(
        await lendStore.getTotalAccountBorrow(market.address)
      );
    }
  };

  const [health, setHealth] = React.useState(new BN(-2));
  const fetchHealth = async () => {
    if (health.eq(-2)) {
      setHealth(await lendStore.getHealthAsync(market.address));
    }
    while (health.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setHealth(await lendStore.getHealthAsync(market.address));
    }
  };

  Promise.all([
    fetchTotalAccountSupply(),
    fetchTotalAccountBorrow(),
    fetchHealth()
  ]);

  const stats = [
    {
      title: "NET APY",
      value: `${lendStore.netApy.toFormat(2)}%`,
      info: "Your annual net profit (expenses) relative to your deposits (loans) USD value.",
      loading: false
    },
    {
      title: "Supply balance",
      value: `$ ${lendStore.accountSupplyBalance.toFormat(2)}`,
      info: "USD value of your deposited assets."
    },
    {
      title: "Borrow balance",
      value: `$ ${lendStore.accountBorrowBalance.toFormat(2)}`,
      info: "USD value of your borrowed assets."
    },
    {
      title: "Daily reward",
      value: `$ ${lendStore.accountSupply
        .reduce((acc, s) => {
          const dailyIncome = BN.formatUnits(s.dailyIncome, s.decimals);
          return acc.plus(dailyIncome.times(s.prices.min));
        }, BN.ZERO)
        .toFormat(2)}`,
      info: "Total daily earnings across all tokens"
    }
  ];
  if (accountStore.address == null || lendStore.accountSupplyBalance.lte(0))
    return null;

  return (
    <Root>
      <HealthSection>
        <CircularProgressbar
          percent={lendStore.health.toDecimalPlaces(2).toNumber()}
        />
        <div>
          <Text weight={500} size="big" nowrap>
            Account Health
          </Text>
          <SizedBox height={4} />
          <Row alignItems="center" justifyContent="center">
            <Text type="secondary" nowrap style={{ cursor: "pointer" }}>
              Learn more
            </Text>
            <SizedBox width={4} />
            <Tooltip content={<Text>{accountHealthInfo}</Text>}>
              <Info />
            </Tooltip>
          </Row>
        </div>
      </HealthSection>
      <StatsContainer>
        {stats.map(({ title, value, loading, info }, idx) => (
          <Column key={idx} >
            <Row alignItems="center" justifyContent="center">
              <Text fitContent type="secondary" size="medium+" nowrap>
                {title}
              </Text>
              <SizedBox width={4} />
              <Tooltip content={<Text>{info}</Text>}>
                <Info />
              </Tooltip>
            </Row>
            <SizedBox height={4} />
            {loading ? (
              <Skeleton height={24} width={64} />
            ) : (
              <Text
                size="bigger"
                weight={500}
                textAlign={width && width >= 780 ? "right" : "left"}
              >
                {value}
              </Text>
            )}
          </Column>
        ))}
      </StatsContainer>
    </Root>
  );
};
export default AccountCard;
